import { useForm, FormProvider } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

import LoginPasswordInput from "components/Auth/LoginPasswordInput";

const schema = z.object({
  password: z.string().min(1, "Please enter your password"),
});

type PasswordVerificationProps = {
  onSubmit: (password: string) => Promise<void>;
  error?: string | null;
  isLoading?: boolean;
};

type FormData = z.infer<typeof schema>;

export default function PasswordVerification({
  onSubmit,
  error,
  isLoading = false,
}: PasswordVerificationProps) {
  const methods = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      password: "",
    },
  });

  const { handleSubmit, control, formState } = methods;

  const isSubmitting = formState.isSubmitting || isLoading;

  const handleSubmitForm = async (data: { password: string }) => {
    await onSubmit(data.password);
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(handleSubmitForm)} className="space-y-6">
        <LoginPasswordInput
          name="password"
          control={control}
          disabled={isSubmitting}
        />

        {error && (
          <div className="rounded bg-red-100 p-3 text-sm text-red-700">
            {error}
          </div>
        )}

        <button
          type="submit"
          className="flex w-full items-center justify-center rounded-lg bg-cantelope px-6 py-3 font-bold text-soil transition-all hover:bg-soil hover:text-cantelope disabled:pointer-events-none disabled:opacity-50"
          disabled={isSubmitting}
        >
          {isSubmitting ? "Verifying..." : "Continue"}
        </button>
      </form>
    </FormProvider>
  );
}
